<template>
  <Modal
    v-model="visible"
    :title="langMap.skeletonGraphDataImport[lang]"
    width="700"
    @on-cancel="handleCancel"
  >
    <div class="import-container">
      <div class="toolbar">
        <Button size="small" @click="formatJSON">{{ langMap.skeletonGraphFormatJson[lang] }}</Button>
        <!-- <Button size="small" @click="pasteFromClipboard">{{ langMap.skeletonGraphPaste[lang] }}</Button> -->
        <Button size="small" @click="clearJSON">{{ langMap.skeletonGraphClear[lang] }}</Button>
        <Upload
          :before-upload="beforeUpload"
          :show-upload-list="false"
          accept=".json"
          action=""
          style="display: inline-block; margin-left: auto;"
        >
          <Button size="small" icon="ios-cloud-upload">
            {{ langMap.clickUpload[lang] }}
          </Button>
        </Upload>
      </div>
      
      <Input
        v-model="jsonInput"
        type="textarea"
        :rows="12"
        :placeholder="langMap.skeletonGraphPlaceholderExample[lang]"
        @on-change="handleJsonInputChange"
      />
      
      <div v-if="parseResult" class="parse-result">
        <Alert v-if="parseResult.errors.length === 0" type="success" show-icon>
          {{ langMap.skeletonGraphDataValidationSuccess[lang].replace('{count}', parseResult.nodes.length) }}
        </Alert>
        <Alert v-else type="error" show-icon>
          {{ langMap.skeletonGraphDataValidationFailed[lang] }}
        </Alert>

        <div class="result-summary">
          <span><strong>{{ langMap.nodeCount[lang] }}:</strong> {{ parseResult.nodes.length }}</span>
        </div>

        <div v-if="parseResult.errors.length > 0" class="error-list">
          <ul>
            <li v-for="(error, index) in parseResult.errors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel">{{ langMap.cancel[lang] }}</Button>
      <Button
        type="primary"
        @click="confirmImport"
        :disabled="!parseResult || parseResult.errors.length > 0"
      >
        {{ langMap.skeletonGraphConfirmAdd[lang] }}
      </Button>
    </div>
  </Modal>
</template>

<script>
import langMap from '@/utils/language';
import { parseJSON, formatJSONString, validateJSONString } from '@/utils/skeletonGraph/dataParser';

export default {
  name: 'DataImportDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      langMap,
      jsonInput: '',
      parseResult: null,
      parseTimer: null
    };
  },
  computed: {
    lang() {
      return this.$store.state.app.language;
    },
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  methods: {
    beforeUpload(file) {
      // 验证文件类型
      if (!file.name.endsWith('.json')) {
        this.$Message.error(this.langMap.skeletonGraphFileFormatError[this.lang]);
        return false;
      }

      // 验证文件大小
      if (file.size > 10 * 1024 * 1024) {
        this.$Message.error(this.langMap.skeletonGraphFileSizeLimit[this.lang]);
        return false;
      }

      // 读取文件内容
      const reader = new FileReader();
      reader.onload = (e) => {
        this.jsonInput = e.target.result;
        this.parseJSONData(this.jsonInput);
      };
      reader.readAsText(file);

      return false; // 阻止自动上传
    },

    handleFileChange(response, file, fileList) {
      // 文件已在beforeUpload中处理
    },

    handleJsonInputChange() {
      // 清除之前的定时器
      if (this.parseTimer) {
        clearTimeout(this.parseTimer);
      }
      
      // 如果输入为空，清空解析结果
      if (!this.jsonInput.trim()) {
        this.parseResult = null;
        return;
      }
      
      // 设置新的定时器，延迟 500ms 后自动解析
      this.parseTimer = setTimeout(() => {
        this.parseJSONData(this.jsonInput);
      }, 500);
    },

    formatJSON() {
      if (!this.jsonInput.trim()) {
        this.$Message.warning(this.langMap.skeletonGraphJsonRequired[this.lang]);
        return;
      }

      try {
        this.jsonInput = formatJSONString(this.jsonInput);
        this.$Message.success(this.langMap.formatSuccess[this.lang]);
      } catch (e) {
        this.$Message.error(e.message);
      }
    },

    async pasteFromClipboard() {
      try {
        const text = await navigator.clipboard.readText();
        this.jsonInput = text;
        this.$Message.success(this.langMap.skeletonGraphPaste[this.lang] + ' - ' + this.langMap.success[this.lang]);
      } catch (e) {
        this.$Message.error(this.langMap.skeletonGraphPaste[this.lang] + ' - ' + this.langMap.failed[this.lang]);
      }
    },

    clearJSON() {
      this.jsonInput = '';
      this.parseResult = null;
    },

    parseJSONData(jsonString) {
      const result = parseJSON(jsonString);
      this.parseResult = result;
    },

    confirmImport() {
      if (this.parseResult && this.parseResult.errors.length === 0) {
        this.$emit('import-success', {
          nodes: this.parseResult.nodes,
          edges: this.parseResult.edges
        });
        this.handleCancel();
      }
    },

    handleCancel() {
      this.visible = false;
      this.jsonInput = '';
      this.parseResult = null;
      if (this.parseTimer) {
        clearTimeout(this.parseTimer);
        this.parseTimer = null;
      }
    }
  }
};
</script>

<style scoped>
.import-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8eaec;
}

.parse-result {
  margin-top: 12px;
}

.result-summary {
  margin: 12px 0;
  padding: 10px;
  background: #f8f8f9;
  border-radius: 4px;
  font-size: 14px;
}

.error-list {
  margin-top: 12px;
}

.error-list ul {
  margin: 8px 0 0 20px;
  color: #ed4014;
  max-height: 150px;
  overflow-y: auto;
}

.error-list li {
  margin: 5px 0;
  font-size: 13px;
}
</style>
